import { Button } from "@design";
import type { ReactNode } from "react";
import { useState } from "react";
import { useNavigate } from "react-router";
import type { SpaceResourceResponse } from "@/api";
import { QuerySection } from "@/components/section";
import { Text } from "@/components/Text";
import { AddItemModal, type AddOption } from "./items/AddItemModal";
import { ShareItemModal } from "./items/ShareItemModal";
import { SpaceItemRow } from "./items/SpaceItemRow";
import { type SpaceItemType, targetOf, useSpaceItems } from "./items/useSpaceItems";

/**
 * Agents or data sources in the space: list (as I see it), add from what I can see,
 * share / make private, give the space access, pin, remove. Sharing first shows who in
 * the space can't use it; its admins can give the space access instead.
 */
export function SpaceItemsList({
  spaceUuid,
  type,
  title,
  addLabel,
  addTitle,
  emptyHint,
  icon,
  openPath,
  options,
  showTitle = true,
}: Readonly<{
  spaceUuid: string;
  type: SpaceItemType;
  title: string;
  addLabel: string;
  addTitle: string;
  emptyHint: string;
  icon: ReactNode;
  openPath: (targetUuid: string) => string;
  options: { data: AddOption[] | undefined; isPending: boolean };
  /** Off when the page title already says it (a section with a single list). */
  showTitle?: boolean;
}>) {
  const navigate = useNavigate();
  const items = useSpaceItems(spaceUuid, type);
  const [adding, setAdding] = useState(false);
  const [sharing, setSharing] = useState<{ item: SpaceResourceResponse; mode: "share" | "grant" } | null>(null);
  const attached = new Set((items.items.data ?? []).map(targetOf));

  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        {showTitle ? (
          <Text as="h2" variant="h3">
            {title}
          </Text>
        ) : (
          <span />
        )}
        <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
          {addLabel}
        </Button>
      </div>
      <QuerySection query={items.items} isEmpty={(list) => list.length === 0} empty={{ icon, hint: emptyHint }}>
        {(list) => (
          <ul className="flex flex-col gap-2">
            {list.map((item) => (
              <li key={item.uuid}>
                <SpaceItemRow
                  item={item}
                  icon={icon}
                  onOpen={() => item.canUse && navigate(openPath(targetOf(item)))}
                  onShare={() => setSharing({ item, mode: "share" })}
                  onGiveAccess={() => setSharing({ item, mode: "grant" })}
                  onRemoveAccess={() => items.removeAccess(item)}
                  onMakePrivate={() => items.setVisibility(item, "private")}
                  onTogglePin={() => items.setPinned(item, !item.isPinned)}
                  onRemove={() => items.remove(item)}
                />
              </li>
            ))}
          </ul>
        )}
      </QuerySection>
      {adding && (
        <AddItemModal
          title={addTitle}
          options={(options.data ?? []).filter((o) => !attached.has(o.uuid))}
          loading={options.isPending}
          onAdd={items.add}
          onClose={() => setAdding(false)}
        />
      )}
      {sharing && (
        <ShareItemModal
          spaceUuid={spaceUuid}
          item={sharing.item}
          mode={sharing.mode}
          onClose={() => setSharing(null)}
          onShare={() => {
            items.setVisibility(sharing.item, "space");
            setSharing(null);
          }}
          onGrant={() => {
            items.giveAccess(sharing.item);
            setSharing(null);
          }}
        />
      )}
    </section>
  );
}
